<script setup lang="ts">
import { ArrowLeftRight, BookOpen, LibraryBig, Plus, User, Users } from '@lucide/vue'
import { computed, type Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PendingNotice from '@/components/PendingNotice.vue'
import { roleLabel } from '@/services/format'
import { useAdminStore } from '@/stores/admin'
import { useAuthStore } from '@/stores/auth'
import { useCatalogStore } from '@/stores/catalog'

/**
 * Navigation (DESIGN.md, direction 1b) : menu latéral au-delà de 768px, barre
 * d'onglets en bas en dessous. Pas d'onglet « Ajouter » sur mobile.
 */
const auth = useAuthStore()
const catalog = useCatalogStore()
const admin = useAdminStore()
const route = useRoute()
const router = useRouter()

interface Tab {
  name: string
  label: string
  short: string
  icon: Component
  count?: number
  badge?: number
}

const myLoans = computed(() => (auth.user ? catalog.borrowedBy(auth.user.uid).length : 0))

const tabs = computed<Tab[]>(() => {
  const list: Tab[] = []
  if (auth.isMember) {
    list.push({ name: 'catalog', label: 'Catalogue', short: 'Catalogue', icon: LibraryBig })
    list.push({ name: 'my-loans', label: 'Mes emprunts', short: 'Emprunts', icon: BookOpen, count: myLoans.value })
  }
  if (auth.isEditor) list.push({ name: 'who-has-what', label: 'Qui a quoi', short: 'Qui a quoi', icon: ArrowLeftRight, count: catalog.stats.borrowed })
  if (auth.isSuperadmin) list.push({ name: 'admin', label: 'Administration', short: 'Admin.', icon: Users, badge: admin.pendingCount })
  return list
})

const isActive = (name: string) =>
  route.name === name || (name === 'catalog' && (route.name === 'book' || route.name === 'book-edit'))

function showLocation(name: string) {
  catalog.location = catalog.location === name ? '' : name
  if (route.name !== 'catalog') void router.push({ name: 'catalog' })
}
</script>

<template>
  <div class="shell" :class="{ 'no-tabs': route.meta.hideTabs }">
    <aside class="side hide-mobile">
      <RouterLink to="/catalogue" class="brand">Ex Libris</RouterLink>
      <div class="xsmall muted sub">Bibliothèque familiale</div>
      <RouterLink v-if="auth.isEditor" :to="{ name: 'add' }" class="btn btn-primary add">
        <Plus :size="16" :stroke-width="1.5" />Ajouter un livre
      </RouterLink>
      <nav class="links" aria-label="Navigation principale">
        <RouterLink v-for="t in tabs" :key="t.name" :to="{ name: t.name }" class="link" :class="{ active: isActive(t.name) }" :aria-current="isActive(t.name) ? 'page' : undefined">
          <component :is="t.icon" :size="18" :stroke-width="1.5" />
          {{ t.label }}
          <span v-if="t.count" class="n tabular">{{ t.count }}</span>
          <span v-if="t.badge" class="count-badge n" :aria-label="`${t.badge} compte(s) en attente`">{{ t.badge }}</span>
        </RouterLink>
      </nav>
      <template v-if="auth.isMember && catalog.locations.length">
        <div class="hr"></div>
        <h6 class="muted">Emplacements</h6>
        <div class="locations">
          <button v-for="l in catalog.locations" :key="l.name" class="loc" :class="{ active: catalog.location === l.name }" @click="showLocation(l.name)">
            <span class="ellipsis">{{ l.name }}</span>
            <span class="muted tabular">{{ l.count }}</span>
          </button>
        </div>
      </template>
      <span class="spacer"></span>
      <RouterLink v-if="auth.isSignedIn" to="/compte" class="me" :class="{ active: route.name === 'account' }">
        <span class="avatar">{{ auth.initial }}</span>
        <div class="me-text">
          <div class="ellipsis">{{ auth.displayName || auth.user?.email }}</div>
          <div class="xsmall muted">{{ roleLabel(auth.role) }}</div>
        </div>
      </RouterLink>
    </aside>

    <main class="main">
      <div v-if="auth.isPending" class="pending"><PendingNotice /></div>
      <slot />
    </main>

    <nav v-if="!route.meta.hideTabs" class="tabs hide-desktop" aria-label="Navigation principale" :style="{ gridTemplateColumns: `repeat(${tabs.length + 1}, 1fr)` }">
      <RouterLink v-for="t in tabs" :key="t.name" :to="{ name: t.name }" class="tab" :class="{ active: isActive(t.name) }" :aria-current="isActive(t.name) ? 'page' : undefined">
        <span class="icon-wrap">
          <component :is="t.icon" :size="22" :stroke-width="1.5" />
          <span v-if="t.badge" class="count-badge tab-badge">{{ t.badge }}</span>
        </span>
        {{ t.short }}
      </RouterLink>
      <RouterLink to="/compte" class="tab" :class="{ active: route.name === 'account' }">
        <span class="icon-wrap"><User :size="22" :stroke-width="1.5" /></span>
        Compte
      </RouterLink>
    </nav>
  </div>
</template>

<style scoped>
.shell {
  min-height: 100dvh;
  display: flex;
}
.main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-bottom: calc(64px + env(safe-area-inset-bottom));
}
.no-tabs .main {
  padding-bottom: 0;
}
.pending {
  padding: var(--space-3) var(--space-4) 0;
}

/* — Menu latéral (1b) — */
.side {
  width: 232px;
  flex: none;
  position: sticky;
  top: 0;
  height: 100dvh;
  overflow-y: auto;
  border-right: 1px solid var(--color-divider);
  padding: var(--space-6) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.brand {
  font: 400 26px/1 var(--font-heading);
  color: var(--color-text);
  text-decoration: none;
  margin-bottom: 4px;
}
.sub {
  margin-bottom: var(--space-6);
}
.add {
  justify-content: flex-start;
  margin-bottom: var(--space-4);
}
.links {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: var(--radius-md);
  text-decoration: none;
  color: var(--color-text);
  font-size: 14px;
}
.link:hover {
  background: var(--color-neutral-200);
}
.link.active {
  color: var(--color-accent-700);
  box-shadow: inset 0 0 0 1px var(--color-accent);
}
.link .n {
  margin-left: auto;
  font-size: 12px;
  color: var(--color-neutral-700);
}
.link .count-badge.n {
  color: var(--color-accent-700);
}
.side h6 {
  margin-top: 4px;
}
.locations {
  display: flex;
  flex-direction: column;
}
.loc {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  padding: 4px 10px;
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
}
.loc:hover {
  background: var(--color-neutral-200);
}
.loc.active {
  color: var(--color-accent-700);
}
.me {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--color-text);
  text-decoration: none;
  padding: 6px;
  margin: 0 -6px;
  border-radius: var(--radius-md);
}
.me:hover,
.me.active {
  background: var(--color-neutral-200);
}
.me-text {
  min-width: 0;
}

/* — Barre d'onglets mobile — */
.tabs {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: grid;
  align-items: end;
  border-top: 1px solid var(--color-divider);
  background: var(--color-bg);
  padding-bottom: max(10px, env(safe-area-inset-bottom));
}
.tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding-top: 9px;
  min-height: 52px;
  font-size: 10.5px;
  color: var(--color-neutral-700);
  text-decoration: none;
}
.tab.active {
  color: var(--color-accent-700);
  box-shadow: inset 0 2px 0 var(--color-accent);
}
.icon-wrap {
  position: relative;
  display: inline-flex;
}
.tab-badge {
  position: absolute;
  top: -5px;
  left: calc(100% - 4px);
}
@media (min-width: 768px) {
  .main {
    padding-bottom: 0;
  }
  .pending {
    padding: var(--space-4) var(--space-8) 0;
  }
}
</style>
